import React, { useState, useMemo, useCallback } from 'react';

interface FinancialIndicatorsProps {
  basicInfo: {
    area: number;
    estimatedRevenue: number;
    fixedRent: number;
    rentPercentage: number;
  };
  operatingCosts: {
    total: number;
  };
  initialCosts: {
    total: number;
  };
  results: {
    estimatedGrossMargin: number;
    monthlyProfitNoDepreciation: number;
    paybackPeriod: number;
    minRevenueFor36MonthPayback: number;
  };
}

const FinancialIndicators: React.FC<FinancialIndicatorsProps> = React.memo(({
  basicInfo,
  operatingCosts,
  initialCosts,
  results
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const formatCurrency = useMemo(() => {
    return (value: number): string => {
      return Math.round(value).toLocaleString('zh-CN');
    };
  }, []);

  const formatPercent = useCallback((value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
  }, []);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const indicators = useMemo(() => {
    const grossMarginRate = basicInfo.estimatedRevenue > 0 ? results.estimatedGrossMargin / basicInfo.estimatedRevenue : 0;
    const breakEvenRevenue = grossMarginRate > 0 ? operatingCosts.total / grossMarginRate : 0;
    const roiValue = initialCosts.total > 0 ? (results.monthlyProfitNoDepreciation * 12) / initialCosts.total : 0;
    const netProfitMarginValue = basicInfo.estimatedRevenue > 0 ? results.monthlyProfitNoDepreciation / basicInfo.estimatedRevenue : 0;
    const revenuePerAreaValue = basicInfo.area > 0 ? basicInfo.estimatedRevenue / basicInfo.area : 0;
    const safetyMarginValue = breakEvenRevenue > 0 ? (basicInfo.estimatedRevenue - breakEvenRevenue) / basicInfo.estimatedRevenue : 0;
    const cashTurnoverRateValue = initialCosts.total > 0 ? basicInfo.estimatedRevenue / initialCosts.total : 0;

    const getIndicatorStatus = (value: number, thresholds: { excellent: number; good: number; average: number }) => {
      if (value >= thresholds.excellent) return { color: 'text-green-700', text: '优秀' };
      if (value >= thresholds.good) return { color: 'text-blue-700', text: '良好' };
      if (value >= thresholds.average) return { color: 'text-yellow-700', text: '一般' };
      return { color: 'text-red-700', text: '较差' };
    };

    return {
      roiValue,
      roiStatus: getIndicatorStatus(roiValue, { excellent: 0.5, good: 0.3, average: 0.15 }),
      netProfitMarginValue,
      netProfitMarginStatus: getIndicatorStatus(netProfitMarginValue, { excellent: 0.25, good: 0.15, average: 0.1 }),
      revenuePerAreaValue,
      revenuePerAreaStatus: getIndicatorStatus(revenuePerAreaValue, { excellent: 3000, good: 2000, average: 1000 }),
      safetyMarginValue,
      safetyMarginStatus: getIndicatorStatus(safetyMarginValue, { excellent: 0.3, good: 0.2, average: 0.1 }),
      cashTurnoverRateValue,
      cashTurnoverRateStatus: getIndicatorStatus(cashTurnoverRateValue, { excellent: 0.3, good: 0.2, average: 0.1 }),
      breakEvenRevenue
    };
  }, [basicInfo, operatingCosts.total, initialCosts.total, results]);

  return (
    <div className="mb-2 animate-fade-in">
      <div className="flex justify-between items-center mb-1 cursor-pointer" onClick={toggleCollapse}>
        <h2 className="section-title title-financial-indicators mb-0 w-full">高级分析指标</h2>
      </div>
      
      {!isCollapsed && (
        <div className="animate-fade-in">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">年化投资回报率(ROI)</div>
              <div className="flex items-baseline">
                <span className="result-value text-gray-800 text-base md:text-lg font-medium">
                  {formatPercent(indicators.roiValue)}
                </span>
                <span className={`ml-1 text-xs font-medium ${indicators.roiStatus.color} bg-white bg-opacity-70 px-1 py-0.5 rounded-full`}>
                  {indicators.roiStatus.text}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-600">一年内投资回报的百分比</div>
            </div>
            
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">保本营业额</div>
              <div className="result-value text-gray-800 text-base md:text-lg font-medium">
                ¥{formatCurrency(indicators.breakEvenRevenue)}<span className="text-xs font-normal">/月</span>
              </div>
              <div className="mt-1 text-xs text-gray-600">不亏不赚的最低销售额</div>
            </div>
            
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">净利润率</div>
              <div className="flex items-baseline">
                <span className="result-value text-gray-800 text-base md:text-lg font-medium">
                  {formatPercent(indicators.netProfitMarginValue)}
                </span>
                <span className={`ml-1 text-xs font-medium ${indicators.netProfitMarginStatus.color} bg-white bg-opacity-70 px-1 py-0.5 rounded-full`}>
                  {indicators.netProfitMarginStatus.text}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-600">营业额中转化为利润的比例</div>
            </div>
            
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">单位面积营收</div>
              <div className="flex items-baseline">
                <span className="result-value text-gray-800 text-base md:text-lg font-medium">
                  ¥{formatCurrency(indicators.revenuePerAreaValue)}
                </span>
                <span className="text-xs font-normal text-gray-600 ml-1">元/㎡·月</span>
                <span className={`ml-1 text-xs font-medium ${indicators.revenuePerAreaStatus.color} bg-white bg-opacity-70 px-1 py-0.5 rounded-full`}>
                  {indicators.revenuePerAreaStatus.text}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-600">店铺空间利用效率</div>
            </div>
            
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">安全边际</div>
              <div className="flex items-baseline">
                <span className="result-value text-gray-800 text-base md:text-lg font-medium">
                  {formatPercent(indicators.safetyMarginValue)}
                </span>
                <span className={`ml-1 text-xs font-medium ${indicators.safetyMarginStatus.color} bg-white bg-opacity-70 px-1 py-0.5 rounded-full`}>
                  {indicators.safetyMarginStatus.text}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-600">销售额可下降的安全空间</div>
            </div>
            
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">现金流周转率</div>
              <div className="flex items-baseline">
                <span className="result-value text-gray-800 text-base md:text-lg font-medium">
                  {formatPercent(indicators.cashTurnoverRateValue)}
                </span>
                <span className={`ml-1 text-xs font-medium ${indicators.cashTurnoverRateStatus.color} bg-white bg-opacity-70 px-1 py-0.5 rounded-full`}>
                  {indicators.cashTurnoverRateStatus.text}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-600">投入资金的使用效率</div>
            </div>
          </div>
          
          <div className="mt-1 rounded-lg p-2 flex items-start">
            <svg className="w-4 h-4 text-yellow-500 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
            </svg>
            <p className="text-xs text-gray-400">
              以上指标基于预估数据计算，仅供参考。不同行业、区域的评估标准可能存在差异，建议结合实际情况综合分析。
            </p>
          </div>
        </div>
      )}
    </div>
  );
});

export default FinancialIndicators;
